import React, { useState, useRef, useEffect } from "react";
import videoAnswerBackground from "../../assets/images/video-answer-background.png";
import "./VideoRecorder.scss";

const VIDEO_QUESTIONS = [
  "Why do you believe you are a strong fit for this position?"
];

function VideoRecorder({ onVideoRecorded, onCancel }) {
  const [step, setStep] = useState("announcement"); // announcement, deviceTest, ready, countdown, recording, review
  const [countdown, setCountdown] = useState(3);
  const [videoSubmitted, setVideoSubmitted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [stream, setStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(60); // 1 minute in seconds
  const [audioLevel, setAudioLevel] = useState(0);
  const [audioLevels, setAudioLevels] = useState(() => Array(5).fill(0));
  const [devices, setDevices] = useState({ cameras: [], microphones: [] });
  const [selectedDevices, setSelectedDevices] = useState({ camera: "", microphone: "" });
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);

  const videoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null); // Use ref to access current recorder in timer
  const countdownIntervalRef = useRef(null);
  const timeLeftRef = useRef(60); // Use ref to track time to prevent state sync issues

  useEffect(() => {
    return () => {
      stopStream();
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []);

  // Effect to check if video is playing and hide loader
  useEffect(() => {
    if (step === "deviceTest" && videoRef.current && streamRef.current) {
      const checkVideoPlaying = () => {
        const video = videoRef.current;
        if (video && !video.paused && video.readyState >= 2 && video.currentTime > 0) {
          console.log("Video is confirmed playing (not paused, ready, has time), hiding loader");
          setIsLoadingDevices(false);
        }
      };
      
      // Check immediately
      checkVideoPlaying();
      
      // Check periodically
      const interval = setInterval(checkVideoPlaying, 300);
      
      // Also check on video events
      if (videoRef.current) {
        const video = videoRef.current;
        const onPlaying = () => {
          console.log("Video playing confirmed via 'playing' event");
          setIsLoadingDevices(false);
        };
        const onTimeUpdate = () => {
          if (!video.paused && video.currentTime > 0) {
            setIsLoadingDevices(false);
          }
        };
        video.addEventListener('playing', onPlaying, { once: true });
        video.addEventListener('timeupdate', onTimeUpdate);
        
        return () => {
          clearInterval(interval);
          video.removeEventListener('playing', onPlaying);
          video.removeEventListener('timeupdate', onTimeUpdate);
        };
      }
      
      return () => clearInterval(interval);
    }
  }, [step, stream, selectedDevices.camera, selectedDevices.microphone]);

  // Effect to ensure video plays when modal opens and stream is available
  useEffect(() => {
    if (step === "deviceTest" && streamRef.current) {
      console.log("useEffect triggered: step=deviceTest, stream exists");
      
      // Single attempt with proper timing
      const timer = setTimeout(() => {
        if (videoRef.current && streamRef.current && step === "deviceTest") {
          const video = videoRef.current;
          
          console.log("useEffect: Setting video stream");
          console.log("Video element exists:", !!video);
          console.log("Stream has video tracks:", streamRef.current.getVideoTracks().length > 0);
          
          // Check if video tracks are active
          const videoTracks = streamRef.current.getVideoTracks();
          videoTracks.forEach(track => {
            console.log(`Track: ${track.label}, enabled: ${track.enabled}, readyState: ${track.readyState}, muted: ${track.muted}`);
          });
          
          // Only set if different to avoid unnecessary updates
          if (video.srcObject !== streamRef.current) {
            video.srcObject = streamRef.current;
            console.log("srcObject set");
          }
          
          video.muted = true;
          video.autoplay = true;
          video.playsInline = true;
          
          // Force play
          const playVideo = () => {
            console.log(`Attempting to play, readyState: ${video.readyState}, paused: ${video.paused}`);
            if (video.paused) {
              video.play()
                .then(() => {
                  console.log("✅ Video playing successfully!");
                  // Use setTimeout to ensure state update happens
                  setTimeout(() => setIsLoadingDevices(false), 100);
                })
                .catch(err => {
                  console.error("❌ Play error:", err.name, err.message);
                  // Retry once
                  setTimeout(() => {
                    if (video.srcObject && video.paused && step === "deviceTest") {
                      video.play()
                        .then(() => {
                          console.log("✅ Video playing on retry!");
                          setTimeout(() => setIsLoadingDevices(false), 100);
                        })
                        .catch(e => {
                          console.error("Retry error:", e);
                          setIsLoadingDevices(false);
                        });
                    }
                  }, 300);
                });
            } else {
              // Already playing
              console.log("Video already playing, hiding loader");
              setTimeout(() => setIsLoadingDevices(false), 50);
            }
          };
          
          // Try to play
          if (video.readyState >= 2) {
            playVideo();
          } else {
            // Wait for video to be ready
            const onLoadedMetadata = () => {
              console.log("useEffect: Video metadata loaded");
              playVideo();
            };
            const onCanPlay = () => {
              console.log("useEffect: Video can play");
              playVideo();
            };
            
            video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
            video.addEventListener('canplay', onCanPlay, { once: true });
            
            // Fallback
            setTimeout(playVideo, 500);
          }
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [step, selectedDevices.camera, selectedDevices.microphone]);

  // Effect for preview video during ready, countdown, and recording steps
  useEffect(() => {
    if (previewVideoRef.current && streamRef.current && (step === "ready" || step === "countdown" || step === "recording")) {
      const video = previewVideoRef.current;
      if (video.srcObject !== streamRef.current) {
        video.srcObject = streamRef.current;
        video.muted = true;
        video.autoplay = true;
        video.playsInline = true;
      }
      
      const playVideo = () => {
        video.play()
          .then(() => {
            console.log("Preview video playing via useEffect");
          })
          .catch(err => {
            console.error("Preview play error:", err);
          });
      };
      
      if (video.readyState >= 2) {
        playVideo();
      } else {
        video.addEventListener('loadedmetadata', playVideo, { once: true });
        video.addEventListener('canplay', playVideo, { once: true });
      }
      
      return () => {
        video.removeEventListener('loadedmetadata', playVideo);
        video.removeEventListener('canplay', playVideo);
      };
    }
  }, [step, stream]);


  const startDeviceTest = async () => {
    try {
      setIsLoadingDevices(true);
      
      // Stop existing stream if any - completely clean up
      if (streamRef.current) {
        console.log("Cleaning up existing stream");
        streamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log(`Stopped track: ${track.label}`);
        });
        streamRef.current = null;
      }
      
      // Clear video elements
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = null;
      }
      
      // Close audio context
      if (audioContextRef.current) {
        if (analyserRef.current && analyserRef.current.animationFrameId) {
          cancelAnimationFrame(analyserRef.current.animationFrameId);
        }
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      
      setStream(null);
      setAudioLevel(0);

      // Optimize video constraints for smaller file size (max 720p)
      const constraints = {
        video: selectedDevices.camera ? { 
          deviceId: { exact: selectedDevices.camera },
          width: { ideal: 1280, max: 1280 },
          height: { ideal: 720, max: 720 },
          frameRate: { ideal: 30, max: 30 }
        } : {
          width: { ideal: 1280, max: 1280 },
          height: { ideal: 720, max: 720 },
          frameRate: { ideal: 30, max: 30 }
        },
        audio: selectedDevices.microphone ? { deviceId: { exact: selectedDevices.microphone } } : true
      };

      console.log("Requesting media access with constraints:", constraints);
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log("Media stream obtained:", mediaStream);
      
      // Verify we got the correct device
      const videoTracks = mediaStream.getVideoTracks();
      const audioTracks = mediaStream.getAudioTracks();
      
      console.log("Video tracks received:", videoTracks.map(t => {
        const settings = t.getSettings();
        return {
          label: t.label,
          deviceId: settings.deviceId,
          requestedDeviceId: selectedDevices.camera
        };
      }));
      
      // Ensure all tracks are enabled
      videoTracks.forEach(track => {
        track.enabled = true;
        const settings = track.getSettings();
        console.log(`Video track: ${track.label}, deviceId: ${settings.deviceId}, enabled: ${track.enabled}`);
      });
      audioTracks.forEach(track => {
        track.enabled = true;
        const settings = track.getSettings();
        console.log(`Audio track: ${track.label}, deviceId: ${settings.deviceId}, enabled: ${track.enabled}`);
      });
      
      setStream(mediaStream);
      streamRef.current = mediaStream;

      // Populate device list now that we have permission (required for labels)
      try {
        const deviceList = await navigator.mediaDevices.enumerateDevices();
        const cameras = deviceList.filter(device => device.kind === 'videoinput');
        const microphones = deviceList.filter(device => device.kind === 'audioinput');
        const videoSettings = mediaStream.getVideoTracks()[0]?.getSettings();
        const audioSettings = mediaStream.getAudioTracks()[0]?.getSettings();
        setDevices({ cameras, microphones });
        setSelectedDevices(prev => ({
          camera: videoSettings?.deviceId ?? cameras[0]?.deviceId ?? prev.camera,
          microphone: audioSettings?.deviceId ?? microphones[0]?.deviceId ?? prev.microphone,
        }));
      } catch (e) {
        console.error("Error enumerating devices:", e);
      }

      // Set step to deviceTest first so modal renders
      setStep("deviceTest");

      // Wait for modal to fully render before setting video stream
      // Use multiple strategies to ensure video element is ready
      const setupVideo = () => {
        if (videoRef.current && streamRef.current) {
          const video = videoRef.current;
          console.log("Setting up video element");
          
          // Clear any existing srcObject first
          if (video.srcObject) {
            const oldStream = video.srcObject;
            oldStream.getTracks().forEach(track => {
              // Don't stop, just clear the reference
            });
          }
          
          // Set the new stream
          video.srcObject = streamRef.current;
          video.muted = true;
          video.autoplay = true;
          video.playsInline = true;
          
          // Force play
          const playVideo = () => {
            video.play()
              .then(() => {
                console.log("Video playing successfully");
                setIsLoadingDevices(false);
              })
              .catch(err => {
                console.error("Play error:", err);
                // Retry
                setTimeout(() => {
                  if (video.srcObject && step === "deviceTest") {
                    video.play()
                      .then(() => {
                        console.log("Video playing on retry");
                        setIsLoadingDevices(false);
                      })
                      .catch(e => console.error("Retry error:", e));
                  }
                }, 300);
              });
          };
          
          // Try to play immediately
          if (video.readyState >= 2) {
            playVideo();
          } else {
            // Wait for video to load
            video.addEventListener('loadedmetadata', playVideo, { once: true });
            video.addEventListener('canplay', playVideo, { once: true });
            setTimeout(playVideo, 500); // Fallback
          }
        } else {
          console.warn("Video ref or stream not ready");
          setIsLoadingDevices(false);
        }
      };

      // Try multiple times to ensure DOM is ready
      requestAnimationFrame(() => {
        setTimeout(setupVideo, 50);
        setTimeout(setupVideo, 200);
        setTimeout(setupVideo, 500);
      });

      // Setup audio level monitoring
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(mediaStream);
        microphone.connect(analyser);
        analyser.fftSize = 2048; // Higher FFT size for better resolution
        analyser.smoothingTimeConstant = 0.5; // More smoothing for less reactive response

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const freqArray = new Uint8Array(analyser.frequencyBinCount);
      const barCount = 5;
      let animationFrameId;
      let lastLevel = 0;
      const prevBarsRef = { current: Array(barCount).fill(0) };

      const updateAudioLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteTimeDomainData(dataArray);
          let sum = 0;
          let max = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const normalized = Math.abs((dataArray[i] - 128) / 128);
            sum += normalized * normalized;
            max = Math.max(max, normalized);
          }
          const rms = Math.sqrt(sum / dataArray.length);
          const combinedLevel = (rms * 0.7 + max * 0.3);
          let level = Math.min(combinedLevel * 4, 1);
          level = lastLevel * 0.5 + level * 0.5;
          lastLevel = level;
          if (level < 0.05 && rms > 0.01) level = Math.max(level, rms * 8);
          level = Math.max(0, Math.min(1, level));
          setAudioLevel(level);

          analyserRef.current.getByteFrequencyData(freqArray);
          const binCount = freqArray.length;
          const newBars = [];
          for (let i = 0; i < barCount; i++) {
            const start = Math.floor((i / barCount) * binCount);
            const end = Math.floor(((i + 1) / barCount) * binCount);
            let s = 0;
            for (let j = start; j < end; j++) s += freqArray[j];
            const raw = (s / (end - start)) / 255;
            const smoothed = prevBarsRef.current[i] * 0.5 + raw * 0.5;
            prevBarsRef.current[i] = smoothed;
            newBars.push(Math.min(1, smoothed * 1.8));
          }
          setAudioLevels(newBars);

          animationFrameId = requestAnimationFrame(updateAudioLevel);
        }
      };
      updateAudioLevel();
      
      // Store animation frame ID for cleanup
      analyserRef.current.animationFrameId = animationFrameId;
      } catch (audioError) {
        console.error("Error setting up audio monitoring:", audioError);
      }

    } catch (error) {
      console.error("Error accessing devices:", error);
      setIsLoadingDevices(false);
      let errorMessage = "Error accessing camera/microphone. ";
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        errorMessage += "Please allow camera and microphone permissions in your browser settings.";
      } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
        errorMessage += "No camera or microphone found. Please connect a device.";
      } else {
        errorMessage += "Please check your device connections and browser permissions.";
      }
      alert(errorMessage);
      // Go back to announcement if device access fails
      setStep("announcement");
    }
  };

  const startRecording = async () => {
    try {
      if (!streamRef.current) {
        await startDeviceTest();
        // Wait a bit for stream to be ready
        setTimeout(() => {
          if (streamRef.current) {
            actuallyStartRecording();
          }
        }, 500);
        return;
      }
      actuallyStartRecording();
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Error starting recording. Please try again.");
    }
  };

  const startCountdown = () => {
    // Clear any existing countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    
    setCountdown(3);
    setStep("countdown");
    
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          actuallyStartRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const actuallyStartRecording = () => {
    if (!streamRef.current) {
      // If no stream, try to get it first
      startDeviceTest().then(() => {
        if (streamRef.current) {
          actuallyStartRecording();
        }
      });
      return;
    }

    // Update preview video - ensure it's set up for recording view
    if (previewVideoRef.current && streamRef.current) {
      previewVideoRef.current.srcObject = streamRef.current;
      previewVideoRef.current.muted = true;
      previewVideoRef.current.autoplay = true;
      previewVideoRef.current.playsInline = true;
      previewVideoRef.current.play()
        .then(() => {
          console.log("Preview video playing");
        })
        .catch(err => {
          console.error("Error playing preview:", err);
          // Retry
          setTimeout(() => {
            if (previewVideoRef.current && previewVideoRef.current.srcObject) {
              previewVideoRef.current.play();
            }
          }, 200);
        });
    }

    // Optimize MediaRecorder settings for smaller file size
    // Try to use optimized codec if available (VP9 is more efficient than VP8)
    const codecs = [
      'video/webm;codecs=vp9,opus', // VP9 is more efficient, smaller file size
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    
    let selectedMimeType = 'video/webm';
    for (const codec of codecs) {
      if (MediaRecorder.isTypeSupported(codec)) {
        selectedMimeType = codec;
        break;
      }
    }
    
    const recorder = new MediaRecorder(streamRef.current, {
      mimeType: selectedMimeType,
      videoBitsPerSecond: 800000, // 800 kbps (reduced from default ~2.5 Mbps) for smaller file size
      audioBitsPerSecond: 64000, // 64 kbps for audio
    });

    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = async () => {
      // Clear timer immediately when recording stops
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      const originalBlob = new Blob(chunks, { type: selectedMimeType });
      
      // Compress video to reduce file size (if needed)
      try {
        const finalBlob = await compressVideo(originalBlob);
        const videoUrl = URL.createObjectURL(finalBlob);
        setRecordedVideo({ blob: finalBlob, url: videoUrl });
        console.log(`Final video size: ${(finalBlob.size / 1024 / 1024).toFixed(2)}MB`);
      } catch (error) {
        console.error("Video processing failed, using original:", error);
        // Fallback to original if processing fails
        const videoUrl = URL.createObjectURL(originalBlob);
        setRecordedVideo({ blob: originalBlob, url: videoUrl });
      }
      
      setStep("review");
      setIsRecording(false);
      mediaRecorderRef.current = null; // Clear ref
    };

    recorder.start();
    setMediaRecorder(recorder);
    mediaRecorderRef.current = recorder; // Store in ref for timer access
    setIsRecording(true);
    timeLeftRef.current = 60; // Reset ref
    setTimeRemaining(60); // 1 minute
    setCountdown(0); // Reset countdown
    setStep("recording");
    
    console.log(`Recording started with codec: ${selectedMimeType}, bitrate: 800 kbps`);

    // Clear any existing timer before starting a new one
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Reset time and start countdown timer
    timeLeftRef.current = 60;
    setTimeRemaining(60);
    
    timerRef.current = setInterval(() => {
      timeLeftRef.current = timeLeftRef.current - 1;
      const currentTime = timeLeftRef.current;
      
      // Update state with current time
      setTimeRemaining(currentTime);
      
      // Check if timer reached 0
      if (currentTime <= 0) {
        // Clear interval first to prevent multiple calls
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        // Stop recording when timer reaches 0
        // Use ref to get current recorder
        const currentRecorder = mediaRecorderRef.current;
        if (currentRecorder && currentRecorder.state !== 'inactive' && currentRecorder.state !== 'stopped') {
          console.log("Auto-stopping recording - timer reached 0");
          currentRecorder.stop();
          setIsRecording(false);
        }
        timeLeftRef.current = 0;
      }
    }, 1000);
  };

  const stopRecording = () => {
    // Clear timer first to prevent any further updates
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    const currentRecorder = mediaRecorderRef.current || mediaRecorder;
    if (currentRecorder && isRecording) {
      // Stop recording
      if (currentRecorder.state !== 'inactive' && currentRecorder.state !== 'stopped') {
        currentRecorder.stop();
      }
      setIsRecording(false);
    }
  };

  const stopStream = () => {
    // Cancel audio level animation
    if (analyserRef.current && analyserRef.current.animationFrameId) {
      cancelAnimationFrame(analyserRef.current.animationFrameId);
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (previewVideoRef.current) {
      previewVideoRef.current.srcObject = null;
    }
    setStream(null);
    setAudioLevel(0);
    setAudioLevels(Array(5).fill(0));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Compress video to reduce file size
  // With 800 kbps bitrate and 720p resolution, 1-minute videos should be ~6-7 MB
  const compressVideo = async (blob) => {
    const originalSize = blob.size;
    console.log(`Original video size: ${(originalSize / 1024 / 1024).toFixed(2)}MB`);
    
    // If file is already small enough (< 10 MB), skip compression
    if (originalSize < 10 * 1024 * 1024) {
      console.log("Video size is acceptable, skipping compression");
      return blob;
    }
    
    // For larger files, return as-is since we're already recording at optimized settings
    // Further compression would require complex re-encoding which may not be worth it
    console.log("Video recorded with optimized settings (720p, 800 kbps)");
    return blob;
  };

  const handleRetake = () => {
    if (videoSubmitted) {
      // If already submitted, don't allow retake
      return;
    }
    
    // Clear any existing timer intervals
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    setRecordedVideo(null);
    setVideoSubmitted(false);
    timeLeftRef.current = 60; // Reset ref
    setTimeRemaining(60); // Reset to 1 minute
    setCountdown(3); // Reset countdown
    setIsRecording(false);
    setStep("deviceTest");
    
    // Restart device test to get fresh stream
    if (streamRef.current) {
      startDeviceTest();
    }
  };

  const handleSubmit = () => {
    if (recordedVideo && recordedVideo.blob) {
      setVideoSubmitted(true);
      onVideoRecorded(recordedVideo.blob);
      stopStream();
    }
  };

  const handleDeviceChange = async (type, deviceId) => {
    console.log(`Switching ${type} to device:`, deviceId);
    
    // Update selected device
    const newDevices = { ...selectedDevices, [type]: deviceId };
    setSelectedDevices(newDevices);
    
    // Stop current stream completely
    if (streamRef.current) {
      console.log("Stopping current stream tracks");
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`Stopped track: ${track.label}, kind: ${track.kind}`);
      });
      streamRef.current = null;
    }
    
    // Clear video elements
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (previewVideoRef.current) {
      previewVideoRef.current.srcObject = null;
    }
    
    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    setStream(null);
    setAudioLevel(0);
    setIsLoadingDevices(true);
    
    // Wait a bit for tracks to fully stop, then get new stream
    setTimeout(async () => {
      try {
        // Optimize video constraints for smaller file size (max 720p)
        const constraints = {
          video: newDevices.camera ? { 
            deviceId: { exact: newDevices.camera },
            width: { ideal: 1280, max: 1280 },
            height: { ideal: 720, max: 720 },
            frameRate: { ideal: 30, max: 30 }
          } : {
            width: { ideal: 1280, max: 1280 },
            height: { ideal: 720, max: 720 },
            frameRate: { ideal: 30, max: 30 }
          },
          audio: newDevices.microphone ? { deviceId: { exact: newDevices.microphone } } : true
        };

        console.log("Requesting new stream with constraints:", constraints);
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log("New media stream obtained");
        
        // Verify we got the right device
        const videoTracks = mediaStream.getVideoTracks();
        const audioTracks = mediaStream.getAudioTracks();
        
        console.log("Video tracks:", videoTracks.map(t => ({ label: t.label, deviceId: t.getSettings().deviceId })));
        console.log("Audio tracks:", audioTracks.map(t => ({ label: t.label, deviceId: t.getSettings().deviceId })));
        
        // Ensure all tracks are enabled
        videoTracks.forEach(track => {
          track.enabled = true;
          console.log(`Video track: ${track.label}, enabled: ${track.enabled}`);
        });
        audioTracks.forEach(track => {
          track.enabled = true;
          console.log(`Audio track: ${track.label}, enabled: ${track.enabled}`);
        });
        
        setStream(mediaStream);
        streamRef.current = mediaStream;

        // Update video element with new stream
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (videoRef.current && streamRef.current) {
              console.log("Setting new stream to video element");
              const video = videoRef.current;
              
              // Clear first, then set new
              video.srcObject = null;
              
              // Small delay to ensure old stream is cleared
              setTimeout(() => {
                if (videoRef.current && streamRef.current) {
                  const videoEl = videoRef.current;
                  videoEl.srcObject = streamRef.current;
                  videoEl.muted = true;
                  videoEl.autoplay = true;
                  videoEl.playsInline = true;
                  
                  const playNewStream = () => {
                    if (videoEl.paused) {
                      videoEl.play()
                        .then(() => {
                          console.log("New video stream playing");
                          setTimeout(() => setIsLoadingDevices(false), 100);
                        })
                        .catch(err => {
                          console.error("Error playing new stream:", err);
                          // Retry
                          setTimeout(() => {
                            if (videoEl && videoEl.srcObject && videoEl.paused) {
                              videoEl.play()
                                .then(() => {
                                  console.log("New video stream playing on retry");
                                  setTimeout(() => setIsLoadingDevices(false), 100);
                                })
                                .catch(e => {
                                  console.error("Retry error:", e);
                                  setIsLoadingDevices(false);
                                });
                            }
                          }, 300);
                        });
                    } else {
                      console.log("New stream already playing");
                      setTimeout(() => setIsLoadingDevices(false), 100);
                    }
                  };
                  
                  // Wait for video to be ready
                  if (videoEl.readyState >= 2) {
                    playNewStream();
                  } else {
                    videoEl.addEventListener('loadedmetadata', playNewStream, { once: true });
                    videoEl.addEventListener('canplay', playNewStream, { once: true });
                    setTimeout(playNewStream, 500);
                  }
                }
              }, 100);
            }
          }, 150);
        });

        // Setup audio level monitoring
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const analyser = audioContext.createAnalyser();
          const microphone = audioContext.createMediaStreamSource(mediaStream);
          microphone.connect(analyser);
          analyser.fftSize = 256;

          audioContextRef.current = audioContext;
          analyserRef.current = analyser;

          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          const barCount = 5;
          let animationFrameId;
          const prevBarsRef = { current: Array(barCount).fill(0) };
          const updateAudioLevel = () => {
            if (analyserRef.current) {
              analyserRef.current.getByteFrequencyData(dataArray);
              const binCount = dataArray.length;
              const average = dataArray.reduce((a, b) => a + b, 0) / binCount;
              setAudioLevel(Math.min(average / 255, 1));
              const newBars = [];
              for (let i = 0; i < barCount; i++) {
                const start = Math.floor((i / barCount) * binCount);
                const end = Math.floor(((i + 1) / barCount) * binCount);
                let s = 0;
                for (let j = start; j < end; j++) s += dataArray[j];
                const raw = (s / (end - start)) / 255;
                const smoothed = prevBarsRef.current[i] * 0.5 + raw * 0.5;
                prevBarsRef.current[i] = smoothed;
                newBars.push(Math.min(1, smoothed * 1.8));
              }
              setAudioLevels(newBars);
              animationFrameId = requestAnimationFrame(updateAudioLevel);
            }
          };
          updateAudioLevel();
          analyserRef.current.animationFrameId = animationFrameId;
        } catch (audioError) {
          console.error("Error setting up audio monitoring:", audioError);
        }
      } catch (error) {
        console.error("Error switching device:", error);
        setIsLoadingDevices(false);
        alert(`Error switching ${type}. Please try again.`);
      }
    }, 200);
  };

  return (
    <div className="video-recorder">
      {step === "announcement" && (
        <div className="video-announcement">
          <div className="announcement-intro">
            <p>
              As part of our application process, we invite you to share a brief video introduction. 
              This allows our hiring team to get to know you beyond your resume and better understand 
              your communication style and enthusiasm for the role. This step is optional—you can 
              skip it and submit your application without a video.
              <br />
            </p>
            <p className="privacy-text">
              Your video will be reviewed by our hiring team and kept confidential in accordance with 
              our privacy policy.
            </p>
          </div>
          <div className="announcement-content" style={{ backgroundImage: `url(${videoAnswerBackground})` }}>
            <p className="question-text-on-image">
              <strong>"Why do you believe you are a strong fit for this position?"</strong>
            </p>
            <div className="button-wrapper">
              <button className="tf-btn start-recording-btn" onClick={startDeviceTest}>
                Start Recording Now
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "deviceTest" && (
        <div className="device-test-modal">
          <div className="modal-overlay" onClick={() => { 
            stopStream(); 
            setStep("announcement");
            setAudioLevel(0);
          }}></div>
          <div className="modal-content">
            <h2>Video and Audio Test</h2>
            <p className="test-instructions">
              Please ensure your video feed is clear and your audio is working properly. The microphone 
              indicator below will show activity when you speak. Once you've confirmed everything looks 
              and sounds good, click "Start Now" to begin recording your response.
            </p>

            <div className="video-preview-frame">
              {streamRef.current ? (
                <>
                  {isLoadingDevices && (
                    <div className="loading-indicator">Loading camera...</div>
                  )}
                  <video 
                    key={`device-test-${selectedDevices.camera}-${selectedDevices.microphone}`}
                    ref={videoRef} 
                    autoPlay 
                    muted 
                    playsInline
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      minHeight: '100%',
                      objectFit: 'cover',
                      backgroundColor: '#000',
                      display: 'block',
                      opacity: isLoadingDevices ? 0 : 1,
                      transition: 'opacity 0.3s'
                    }}
                    onLoadedMetadata={(e) => {
                      console.log("Video metadata loaded, readyState:", e.target.readyState);
                      const video = e.target;
                      if (video.srcObject) {
                        video.play()
                          .then(() => {
                            console.log("Playing from onLoadedMetadata");
                            setIsLoadingDevices(false);
                          })
                          .catch(err => {
                            console.error("Play error from onLoadedMetadata:", err);
                            // Retry
                            setTimeout(() => {
                              if (video.srcObject) {
                                video.play()
                                  .then(() => setIsLoadingDevices(false))
                                  .catch(e => console.error("Retry play error:", e));
                              }
                            }, 300);
                          });
                      }
                    }}
                    onCanPlay={(e) => {
                      console.log("Video can play, readyState:", e.target.readyState);
                      const video = e.target;
                      if (video.srcObject) {
                        if (video.paused) {
                          video.play()
                            .then(() => {
                              console.log("Playing from onCanPlay");
                              setIsLoadingDevices(false);
                            })
                            .catch(err => console.error("Play error from onCanPlay:", err));
                        } else {
                          console.log("Video already playing, hiding loader");
                          setIsLoadingDevices(false);
                        }
                      }
                    }}
                    onLoadedData={(e) => {
                      console.log("Video data loaded");
                      const video = e.target;
                      if (video.srcObject) {
                        if (video.paused) {
                          video.play()
                            .then(() => {
                              console.log("Playing from onLoadedData");
                              setIsLoadingDevices(false);
                            })
                            .catch(err => console.error("Play error:", err));
                        } else {
                          setIsLoadingDevices(false);
                        }
                      }
                    }}
                    onPlay={() => {
                      console.log("Video is now playing (onPlay)");
                      setIsLoadingDevices(false);
                    }}
                    onPlaying={() => {
                      console.log("Video is playing (onPlaying event)");
                      setIsLoadingDevices(false);
                    }}
                    onTimeUpdate={() => {
                      // If video is playing and has time updates, it's working
                      if (!isLoadingDevices && videoRef.current && !videoRef.current.paused) {
                        // Video is definitely playing
                      }
                    }}
                    onError={(e) => {
                      console.error("Video error:", e);
                      setIsLoadingDevices(false);
                    }}
                  ></video>
                </>
              ) : (
                <div className="loading-indicator">Waiting for camera...</div>
              )}
            </div>

            <div className="audio-level-indicator audio-slider">
              <svg className="mic-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
              <div className="audio-slider-track">
                <div className="audio-slider-fill" style={{ width: `${Math.min(Math.max(audioLevel * 100, 0), 100)}%` }} />
                <div className="audio-slider-thumb" style={{ left: `${Math.min(Math.max(audioLevel * 100, 0), 100)}%` }} />
              </div>
            </div>

            <div className="device-selectors">
              <select
                className="device-select"
                value={selectedDevices.camera}
                onChange={(e) => handleDeviceChange('camera', e.target.value)}
              >
                {devices.cameras.map(camera => (
                  <option key={camera.deviceId} value={camera.deviceId}>
                    {camera.label || `Camera ${devices.cameras.indexOf(camera) + 1}`}
                  </option>
                ))}
              </select>

              <select
                className="device-select"
                value={selectedDevices.microphone}
                onChange={(e) => handleDeviceChange('microphone', e.target.value)}
              >
                {devices.microphones.map(mic => (
                  <option key={mic.deviceId} value={mic.deviceId}>
                    {mic.label || `Microphone ${devices.microphones.indexOf(mic) + 1}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button className="tf-btn secondary" onClick={() => { 
                stopStream(); 
                setStep("announcement");
                setAudioLevel(0);
              }}>
                Cancel
              </button>
              <button className="tf-btn primary" onClick={() => setStep("ready")}>
                Start Now
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "ready" && (
        <div className="ready-view">
          <div className="ready-video-frame">
            {streamRef.current ? (
              <video 
                ref={previewVideoRef} 
                autoPlay 
                muted 
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              ></video>
            ) : (
              <div className="loading-indicator">Loading preview...</div>
            )}
            <div className="question-overlay">
              <div className="question-text">{VIDEO_QUESTIONS[currentQuestionIndex]}</div>
            </div>
            <div className="start-recording-overlay">
              <div className="start-recording-tooltip">Press to start recording</div>
              <button className="start-recording-btn" onClick={startCountdown}>
                <span className="start-icon"></span>
              </button>
            </div>
          </div>
        </div>
      )}

      {step === "countdown" && (
        <div className="countdown-view">
          <div className="countdown-video-frame">
            {streamRef.current ? (
              <video 
                ref={previewVideoRef} 
                autoPlay 
                muted 
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover', backgroundColor: '#000' }}
              ></video>
            ) : (
              <div className="loading-indicator">Loading preview...</div>
            )}
            {countdown > 0 && (
              <div className="countdown-overlay">
                <div className="countdown-number">{countdown}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === "recording" && (
        <div className="recording-view">
          <div className="recording-video-frame">
            <video 
              ref={previewVideoRef} 
              autoPlay 
              muted 
              playsInline
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                backgroundColor: '#000',
                display: 'block'
              }}
              onLoadedMetadata={() => {
                console.log("Preview metadata loaded");
                if (previewVideoRef.current) {
                  previewVideoRef.current.play().catch(err => console.error("Play error:", err));
                }
              }}
              onCanPlay={() => {
                console.log("Preview can play");
                if (previewVideoRef.current) {
                  previewVideoRef.current.play().catch(err => console.error("Play error:", err));
                }
              }}
            ></video>
            
            {/* Question overlay at top */}
            <div className="question-overlay">
              <div className="question-text">{VIDEO_QUESTIONS[currentQuestionIndex]}</div>
            </div>
            
            {/* Top-left recording indicator and timer */}
            {isRecording && (
              <div className="recording-status-bar">
                <div className="rec-indicator">
                  <span className="rec-dot"></span>
                  <span className="rec-text">REC</span>
                </div>
                <div className="recording-timer">
                  <i className="icon-time"></i>
                  <span className="timer-display">{formatTime(timeRemaining)}</span>
                </div>
              </div>
            )}

            {/* Top-right audio level bars (mic feed) */}
            {isRecording && (
              <div className="recording-audio-bars" aria-hidden>
                <div className="recording-audio-bars-inner">
                  {audioLevels.map((level, i) => (
                    <div
                      key={i}
                      className="recording-audio-bar"
                      style={{ height: `${Math.max(12, level * 100)}%` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Bottom-center stop recording button */}
            {isRecording && (
              <div className="stop-recording-overlay">
                <div className="stop-recording-tooltip">Press to stop recording</div>
                <button className="stop-recording-btn" onClick={stopRecording}>
                  <span className="stop-icon"></span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {step === "review" && recordedVideo && (
        <div className="review-view">
          <div className="review-header">
            <h3>Review Your Recording</h3>
          </div>
          <div className="review-video-frame">
            <video src={recordedVideo.url} controls></video>
          </div>
          <div className="review-actions">
            {!videoSubmitted ? (
              <>
                <button 
                  className="tf-btn retake-btn" 
                  onClick={handleRetake}
                  disabled={videoSubmitted}
                >
                  Retake
                </button>
                <button 
                  className="tf-btn submit-btn" 
                  onClick={handleSubmit}
                  disabled={videoSubmitted}
                >
                  Looks Good
                </button>
              </>
            ) : (
              <div className="submission-message">
                <p><i className="icon-check"></i> Your video has been recorded successfully. Now you can submit your application. Thank you!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoRecorder;
